import type {CSSProperties} from 'react'
import {FC, useEffect, useMemo, useState} from 'react'
import {useFormContext} from 'react-hook-form'
import {Column} from 'react-table'
import {KTable, KTableHeader, KTableLoading} from '../../../../../../components/UI'
import {InputDateWrapper, SelectWrapper} from '../../../../../../components/UI/Form/Input'
import {KTableProvider, useKTable} from '../../../../../../contexts/KTableProvider'
import {useMultiUser} from '../../../../hooks/limpieza/useMultiUser'
import {
  ServicePendingDto,
  TotalesPorEstadoAggregatedDto,
  TotalesPorEstadoFilterDto,
  TotalesPorEstadoItem,
} from '../../../../model/multiUserCrew.model'
import {MULTI_USER_CREW_SERVICES} from '../../../../services/multi-user-crew.service'
import {useTap} from '../../../../../../hooks/useTap'
interface Props {
  prevStep: () => void
  nextStep: () => void
  stepper: any
  goToFirstStep: () => void
  goToStep: (index: number) => void
}

type BucketCrewEstado = 'PENDIENTE' | 'COMPLETADO' | 'OBSERVADO' | 'OTRO'

type TotalesChipKey = 'totales' | 'pendiente' | 'completados' | 'observados'

const bucketDesdeLabelEstado = (label: string): BucketCrewEstado => {
  const u = (label || '').toUpperCase()
  if (u.includes('PENDIENTE')) return 'PENDIENTE'
  if (u.includes('COMPLETAD')) return 'COMPLETADO'
  if (u.includes('OBSERV')) return 'OBSERVADO'
  return 'OTRO'
}

function isTotalesAggregatedDto(raw: object): raw is TotalesPorEstadoAggregatedDto {
  return (
    'TotalPendientes' in raw ||
    'TotalCompletados' in raw ||
    'TotalObservados' in raw ||
    'TotalGeneral' in raw
  )
}

function aggregateItemsToTotalesDto(items: TotalesPorEstadoItem[]): TotalesPorEstadoAggregatedDto {
  const acum: Record<BucketCrewEstado, number> = {
    PENDIENTE: 0,
    COMPLETADO: 0,
    OBSERVADO: 0,
    OTRO: 0,
  }
  items.forEach((item) => {
    const raw = item.EstadoEscaneoActividad || item.Estado || ''
    const c = item.Cantidad ?? item.Total ?? 0
    acum[bucketDesdeLabelEstado(raw)] += c
  })
  const suma = acum.PENDIENTE + acum.COMPLETADO + acum.OBSERVADO + acum.OTRO
  return {
    TotalPendientes: acum.PENDIENTE,
    TotalCompletados: acum.COMPLETADO,
    TotalObservados: acum.OBSERVADO,
    TotalGeneral: suma,
  }
}

/**
 * Mantiene la forma de objeto del API cuando viene agregado; si viene arreglo o `Registros`, compone el mismo objeto.
 */
function parseTotalesPorEstadoData(raw: unknown): TotalesPorEstadoAggregatedDto | null {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>
    if (Array.isArray(o.Registros)) {
      return aggregateItemsToTotalesDto(o.Registros as TotalesPorEstadoItem[])
    }
    if (isTotalesAggregatedDto(o)) {
      return {
        TotalPendientes: Number(o.TotalPendientes ?? 0),
        TotalCompletados: Number(o.TotalCompletados ?? 0),
        TotalObservados: Number(o.TotalObservados ?? 0),
        TotalGeneral: Number(o.TotalGeneral ?? 0),
      }
    }
  }
  if (Array.isArray(raw) && raw.length > 0) {
    return aggregateItemsToTotalesDto(raw as TotalesPorEstadoItem[])
  }
  return null
}

function normalizeEstadosForApi(raw: string[] | undefined | null): string[] {
  if (!raw || !Array.isArray(raw) || raw.length === 0) return []
  return raw.filter((x) => x != null && String(x).trim() !== '')
}

/** Ids de estado para getListPendientes (`Estados`). */
const CREW_ESTADO_API: Record<'pendiente' | 'completados' | 'observados', string> = {
  pendiente: '1',
  completados: '2',
  observados: '3',
}

/** Estilos por chip (alineados con los colores de fila de la tabla). */
const CREW_TOTALES_CHIP_CLASS: Record<TotalesChipKey, string> = {
  totales: 'border border-primary text-primary',
  pendiente: 'border border-gray-300 text-gray-700',
  completados: 'border border-success text-success',
  observados: 'border border-warning text-warning',
}

/** Fondo cuando el chip está seleccionado por doble clic. */
const CREW_TOTALES_CHIP_ACTIVE_CLASS: Record<TotalesChipKey, string> = {
  totales: 'bg-light-danger text-primary border-primary',
  pendiente: 'bg-light text-dark border-gray-400',
  completados: 'bg-light-success text-success border-success',
  observados: 'bg-light-warning text-warning border-warning',
}

const crewTotalesChipBaseStyle: CSSProperties = {
  transition: 'box-shadow 0.22s ease, transform 0.22s ease',
}

const crewTotalesChipHover = (el: HTMLDivElement) => {
  el.style.boxShadow = '0 0.4rem 1.15rem rgba(0, 0, 0, 0.14)'
  el.style.transform = 'translateY(-2px)'
}

const crewTotalesChipHoverEnd = (el: HTMLDivElement) => {
  el.style.boxShadow = ''
  el.style.transform = ''
}

const TableProgrammingContent = () => {
  const {setPagination, loading, setLoading, pagination, data, setData} = useKTable()
  const {setValue, setError, watch, handleSubmit} = useFormContext()
  const {
    getCampaments,
    campaments,
    getModules,
    modules,
    clearModules,
    getRooms,
    rooms,
    clearRooms,
    schedules: programmingServices,
    clearSchedules: clearProgrammingServices,
    getSchedulesService: getProgrammingServices,
  } = useMultiUser()

  const handleTap = useTap()

  const [searching, setSearching] = useState(false)
  const [totalesPorEstado, setTotalesPorEstado] = useState<TotalesPorEstadoAggregatedDto | null>(
    null,
  )
  const [loadingTotales, setLoadingTotales] = useState(false)
  const totalesCrewOrdenados = useMemo(() => {
    const t = totalesPorEstado
    if (!t) return []
    return [
      {key: 'totales' as const, label: 'Totales', count: t.TotalGeneral ?? 0},
      {key: 'pendiente' as const, label: 'Pendiente', count: t.TotalPendientes ?? 0},
      {key: 'completados' as const, label: 'Completados', count: t.TotalCompletados ?? 0},
      {key: 'observados' as const, label: 'Observados', count: t.TotalObservados ?? 0},
    ]
  }, [totalesPorEstado])
  const searchProgramminCounter = watch('searchProgrammingCounter')
  const estadosSeleccionados = normalizeEstadosForApi(watch('Estados'))
  const activeTotalesChipKey: TotalesChipKey = useMemo(() => {
    if (estadosSeleccionados.length === 0) return 'totales'
    const first = estadosSeleccionados[0]
    if (first === CREW_ESTADO_API.pendiente) return 'pendiente'
    if (first === CREW_ESTADO_API.completados) return 'completados'
    if (first === CREW_ESTADO_API.observados) return 'observados'
    return 'totales'
  }, [estadosSeleccionados])
  const validate =
    watch('CampamentoId') &&
    watch('ProgramacionServicioId') &&
    watch('FechaInicio') &&
    watch('FechaFin')

  useEffect(() => {
    getCampaments({isCombo: true}, 1)
  }, [])

  useEffect(() => {
    if (campaments.length === 1) {
      setValue('CampamentoId', campaments[0].value)
      setError('CampamentoId', {})
      getProgrammingServices({CampamentoId: campaments[0].value, pageNumber: 1, size: 100})
    }
  }, [campaments])

  useEffect(() => {
    if (programmingServices.length === 1) {
      setValue('ProgramacionServicioId', programmingServices[0].value)
      setError('ProgramacionServicioId', {})
      getModules({ServiceSchedulingId: programmingServices[0].value})
    }
  }, [programmingServices])

  useEffect(() => {
    if (modules.length === 2) {
      setValue('ModuloId', modules[1].value)
      setError('ModuloId', {})
      getRooms({ServiceSchedulingId: watch('ProgramacionServicioId'), ModuloId: modules[1].value})
    }
  }, [modules])

  useEffect(() => {
    if (rooms.length === 2) {
      setValue('HabitacionId', rooms[1].value)
      setError('HabitacionId', {})
    }
  }, [rooms])

  const handleCampamentChange = (val: any) => {
    let value = val ? val.value : ''
    setValue('CampamentoId', value)
    setError('CampamentoId', {})
    setValue('ProgramacionServicioId', '')
    setValue('ModuloId', '')
    setValue('HabitacionId', '')
    clearProgrammingServices()
    clearModules()
    clearRooms()
    getProgrammingServices({CampamentoId: value, pageNumber: 1, size: 100})
  }

  const handleServiceChange = (val: any) => {
    let value = val ? val.value : ''
    setValue('ProgramacionServicioId', value)
    setError('ProgramacionServicioId', {})
    clearModules()
    clearRooms()
    getModules({ServiceSchedulingId: value})
  }

  const handleModuleChange = (val: any) => {
    let value = val ? val.value : ''
    setValue('ModuloId', value)
    setError('ModuloId', {})
    setValue('HabitacionId', '')
    clearRooms()
    getRooms({ServiceSchedulingId: watch('ProgramacionServicioId'), ModuloId: value})
  }

  const handleRoomChange = (val: any) => {
    let value = val ? val.value : ''
    setValue('HabitacionId', value)
    setError('HabitacionId', {})
  }

  // const handleStatusChange = (val: any) => {
  //   setValue('Estados', val)
  //   setError('Estados', {})
  // }

  // const handlePagination = (filters: ScheduleAssignmentFilter, total: number) => {
  //   setPagination({
  //     currentPage: filters.pageNumber || 1,
  //     perPage: filters.size || 10,
  //     totalResults: total || 0,
  //   })
  // }

  const buildPendingFilters = (estadosForApi: string[], pageNumber: number): ServicePendingDto => {
    const fechaInicio = watch('FechaInicio')
    const fechaFin = watch('FechaFin')
    const campamentoId = watch('CampamentoId')
    const programacionServicioId = watch('ProgramacionServicioId')
    const moduloId = watch('ModuloId')
    const habitacionId = watch('HabitacionId')
    const isCampamentoTodos = !campamentoId || campamentoId === null || campamentoId === ''

    return {
      ...(isCampamentoTodos ? {} : {CampamentoId: campamentoId}),
      ...(isCampamentoTodos ? {} : {ServiceSchedulingId: programacionServicioId}),
      ...(moduloId ? {ModuloId: moduloId} : {}),
      ...(habitacionId ? {HabitacionId: habitacionId} : {}),
      ...(estadosForApi.length > 0 ? {Estados: estadosForApi} : {}),
      FechaIni: fechaInicio,
      FechaFin: fechaFin,
      pageNumber,
      size: pagination.perPage,
    }
  }

  const buildTotalesFilters = (): TotalesPorEstadoFilterDto => {
    const fechaInicio = watch('FechaInicio')
    const fechaFin = watch('FechaFin')
    const campamentoId = watch('CampamentoId')
    const programacionServicioId = watch('ProgramacionServicioId')
    const moduloId = watch('ModuloId')
    const habitacionId = watch('HabitacionId')
    const isCampamentoTodos = !campamentoId || campamentoId === null || campamentoId === ''

    return {
      ...(isCampamentoTodos ? {} : {CampamentoId: campamentoId}),
      ...(isCampamentoTodos ? {} : {ServiceSchedulingId: programacionServicioId}),
      ...(moduloId ? {ModuloId: moduloId} : {}),
      ...(habitacionId ? {HabitacionId: habitacionId} : {}),
      FechaIni: fechaInicio,
      FechaFin: fechaFin,
    }
  }

  const fetchTotalesPorEstado = async () => {
    if (!validate) {
      setTotalesPorEstado(null)
      return
    }
    try {
      setLoadingTotales(true)
      const {data: response} = await MULTI_USER_CREW_SERVICES.getListTotalesPorEstado(
        buildTotalesFilters(),
      )
      if (!response.error) {
        const parsed = parseTotalesPorEstadoData(response.data)
        setTotalesPorEstado(parsed ?? null)
      } else {
        setTotalesPorEstado(null)
      }
    } catch {
      setTotalesPorEstado(null)
    } finally {
      setLoadingTotales(false)
    }
  }

  const handleSearch = async (opts?: {estadosExplicit?: string[]; pageNumber?: number}) => {
    await fetchTotalesPorEstado()
    const estadosForApi =
      opts?.estadosExplicit !== undefined
        ? opts.estadosExplicit
        : normalizeEstadosForApi(watch('Estados'))
    const pageNumber = opts?.pageNumber ?? pagination.currentPage
    if (opts?.pageNumber != null) {
      setPagination((p) => ({...p, currentPage: opts.pageNumber!}))
    }
    try {
      setSearching(true)
      setLoading(true)

      const filters = buildPendingFilters(estadosForApi, pageNumber)
      const {data: response} = await MULTI_USER_CREW_SERVICES.getListPendingScan(filters)
      if (!response.error) {
        setData(response.data)
        setPagination((p) => ({
          ...p,
          totalResults: response.total,
        }))
      }
      setValue('searchProgrammingCounter', searchProgramminCounter + 1)
    } catch (error) {
      console.error('Error al buscar programaciones:', error)
      setData([])
      setTotalesPorEstado(null)
      setPagination({
        currentPage: 1,
        perPage: 10,
        totalResults: 0,
      })
    } finally {
      setSearching(false)
      setLoading(false)
    }
  }

  const handleTotalesChipDoubleClick = (key: TotalesChipKey) => {
    if (!validate) return
    let next: string[] = []
    if (key === 'totales') {
      next = [CREW_ESTADO_API.pendiente, CREW_ESTADO_API.completados, CREW_ESTADO_API.observados]
    } else if (key === 'pendiente') {
      next = [CREW_ESTADO_API.pendiente]
    } else if (key === 'completados') {
      next = [CREW_ESTADO_API.completados]
    } else {
      next = [CREW_ESTADO_API.observados]
    }
    setValue('Estados', next)
    setError('Estados', {})
    void handleSearch({estadosExplicit: next, pageNumber: 1})
  }

  const handleClear = () => {
    // Limpiar campos de fecha y programación de servicio
    setValue('FechaInicio', '')
    setValue('FechaFin', '')
    setValue('CampamentoId', '')
    setValue('SiteId', '')
    setValue('ClienteId', '')
    clearProgrammingServices()
    setValue('ProgramacionServicioId', '')
    setValue('ModuloId', '')
    setValue('HabitacionId', '')
    clearModules()
    clearRooms()
    setValue('searchProgrammingCounter', 0)
    setValue('Estados', [])

    setTotalesPorEstado(null)

    // Limpiar tabla
    setData([])
    setPagination({
      currentPage: 1,
      perPage: 10,
      totalResults: 0,
    })
  }

  useEffect(() => {
    if (searchProgramminCounter > 0 && validate) {
      handleSearch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.perPage])

  const columns: Column<any>[] = useMemo(
    () => [
      {
        Header: (props) => <KTableHeader tableProps={props} title='Módulo' />,
        accessor: 'Modulo',
        Cell: ({value}) => value || '-',
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Habitación' />,
        accessor: 'Habitacion',
        Cell: ({value}) => value || '-',
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Fecha' />,
        accessor: 'FechaInicio',
        Cell: ({value}) => (value ? new Date(value).toLocaleDateString('es-ES') : '-'),
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Hora Inicio' />,
        accessor: 'HoraInicio',
        Cell: ({value}) => value || '-',
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Hora Fin' />,
        accessor: 'HoraFin',
        Cell: ({value}) => value || '-',
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Servicio Limpieza' />,
        accessor: 'Actividad',
        Cell: ({value}) => value || '-',
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Estado' />,
        accessor: 'EstadoEscaneoActividad',
        Cell: ({value}) => value || '-',
      },
      // {
      //   Header: (props) => <KTableHeader tableProps={props} title='Motivo Observacion' />,
      //   accessor: 'Motivo',
      //   Cell: ({value}) => value || '-',
      // },
    ],
    [],
  )

  return (
    <>
      <div className='d-flex row mb-1'>
        <div className='mb-2 col-sm-12 col-md-6'>
          <SelectWrapper
            labelText='Campamento'
            name='CampamentoId'
            data={campaments}
            onChangeValue={handleCampamentChange}
            required
          />
        </div>
        <div className='mb-2 col-sm-12 col-md-6'>
          <SelectWrapper
            labelText='Programación de Servicio'
            name='ProgramacionServicioId'
            data={programmingServices}
            onChangeValue={handleServiceChange}
            required
          />
        </div>
        <div className='mb-2 col-sm-12 col-md-6'>
          <SelectWrapper
            labelText='Módulo'
            name='ModuloId'
            data={modules}
            onChangeValue={handleModuleChange}
          />
        </div>
        <div className='mb-2 col-sm-12 col-md-6'>
          <SelectWrapper
            labelText='Habitación'
            name='HabitacionId'
            data={rooms}
            onChangeValue={handleRoomChange}
          />
        </div>
        <div className='mb-2 col-sm-12 col-md-6'>
          <InputDateWrapper labelText='Fecha Inicio' name='FechaInicio' required />
        </div>
        <div className='mb-2 col-sm-12 col-md-6'>
          <InputDateWrapper labelText='Fecha Fin' name='FechaFin' required />
        </div>

        <div className='mb-4 col-sm-12 d-flex align-items-end gap-2'>
          <button
            type='button'
            className='btn btn-sm btn-primary'
            onClick={handleSubmit(() => void handleSearch())}
            disabled={searching}
          >
            {!searching && <span className='indicator-label'>Buscar</span>}
            {searching && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Buscando...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
          <button
            type='button'
            className='btn btn-sm btn-light'
            onClick={handleClear}
            disabled={searching}
          >
            Limpiar
          </button>
        </div>
      </div>
      {(loadingTotales || totalesPorEstado != null) && (
        <div className='py-1 mb-2'>
          {loadingTotales ? (
            <span className='spinner-border spinner-border-sm text-primary' role='status' />
          ) : (
            <div className='d-flex flex-wrap gap-3'>
              {totalesCrewOrdenados.map((item) => (
                <div
                  key={item.key}
                  className={`rounded px-4 py-2 flex-grow-1 user-select-none cursor-pointer shadow-sm ${
                    item.key === activeTotalesChipKey
                      ? CREW_TOTALES_CHIP_ACTIVE_CLASS[item.key]
                      : CREW_TOTALES_CHIP_CLASS[item.key]
                  }`}
                  style={crewTotalesChipBaseStyle}
                  title='Doble clic para filtrar la tabla por este estado'
                  onMouseEnter={(e) => crewTotalesChipHover(e.currentTarget)}
                  onMouseLeave={(e) => crewTotalesChipHoverEnd(e.currentTarget)}
                  onClick={() =>
                    handleTap(
                      () => {},
                      () => handleTotalesChipDoubleClick(item.key),
                    )
                  }
                >
                  <span className='d-block  fw-semibold'>{item.label}</span>
                  <span className='d-block ms-2 fs-5 fw-bold'>{item.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className='fv-row position-relative'>
        {loading ? <KTableLoading title='Cargando...' /> : null}
        <KTable
          data={data}
          columns={columns}
          paddingTable={false}
          showButtons={false}
          showPageSizeSelector
          getRowClassName={(row) => {
            if (row.original.EstadoEscaneoActividad === 'COMPLETADO') return 'bg-light-success'
            if (row.original.EstadoEscaneoActividad === 'OBSERVADO') return 'bg-light-warning'
            if (row.original.EstadoEscaneoActividad === 'PENDIENTE') return 'bg-light'
            return ''
          }}
        />
      </div>
      {/* <pre>{JSON.stringify(watch(), null, 2)}</pre> */}
    </>
  )
}

export const ProgrammingStep: FC<Props> = ({
  prevStep,
  nextStep,
  stepper,
  goToFirstStep,
  goToStep,
}) => {
  const {watch} = useFormContext()

  const validate =
    watch('CampamentoId') &&
    watch('ProgramacionServicioId') &&
    watch('FechaInicio') &&
    watch('FechaFin')

  const handleNext = () => {
    nextStep()
  }

  const handlePrev = () => {
    prevStep() // Volver a Censo sin resetear el formulario
  }

  return (
    <div className='w-100 pb-20 pb-lg-0'>
      <div className='pb-10 pb-lg-15'>
        <KTableProvider>
          <TableProgrammingContent />
        </KTableProvider>
      </div>
      <div className='d-none d-lg-flex flex-stack pt-15 justify-content-between'>
        <button
          type='button'
          className='btn btn-sm btn-light-primary me-3'
          data-kt-stepper-action='previous'
          onClick={handlePrev}
        >
          Atras
        </button>
        <button
          type='button'
          className='btn btn-sm btn-primary me-3'
          onClick={handleNext}
          disabled={!validate}
        >
          Continuar
        </button>
      </div>
      <div
        className='d-flex d-lg-none position-fixed bottom-0 start-0 end-0 bg-white border-top px-4 py-3 justify-content-between'
        style={{zIndex: 105}}
      >
        <button
          type='button'
          className='btn btn-sm btn-light-primary me-3 flex-grow-1'
          data-kt-stepper-action='previous'
          onClick={handlePrev}
        >
          Atras
        </button>
        <button
          type='button'
          className='btn btn-sm btn-primary flex-grow-1'
          onClick={handleNext}
          disabled={!validate}
        >
          Continuar
        </button>
      </div>
    </div>
  )
}
